import type { FloatProps } from '@chakra-ui/react';
import { Avatar as AvatarPrimitive, Box, Float } from '@chakra-ui/react';
import { StateEnum } from '@halo/chat-sdk/src/sdk/types';
import { ErrorIcon } from '../../assets/icons';
import DefAvatarIcon from '../../assets/icons/defAvatar-icon.svg';
import DeletedAccountIcon from '../../assets/img/Deleted-Account.png';

// eslint-disable-next-line react-refresh/only-export-components
export const baseSizeMap = {
  xs: 32,
  sm: 36,
  md: 40,
  lg: 44,
  xl: 48,
  '2xl': 64,
  '3xl': 88,
};
export interface AvatarProps extends AvatarPrimitive.RootProps {
  src?: string;
  name?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  iconFloatProps?: FloatProps;
  showUserError?: boolean;
  isGroup?: boolean;
  /**
   * @description giveWidth
   * @example calc(var(--text-scale-factor, 0px) + 16px)
   */
  giveWidth?: string;
  floatFontSize?: string;
  activeShake?: boolean;
}

export const Avatar = memo(function Avatar({
  src,
  name,
  onClick,
  size = 'sm',
  icon,
  iconFloatProps,
  showUserError = false,
  giveWidth,
  floatFontSize = '0.75em',
  activeShake = true,
  ...props
}: AvatarProps) {
  const computedBoxSize = useMemo(() => {
    const base = baseSizeMap[(size as keyof typeof baseSizeMap) ?? 'sm'] ?? 32;
    return `calc(${base}px + var(--text-scale-factor, 0px))`;
  }, [size]);

  return (
    <AvatarPure
      src={src}
      className="AvatarRoot"
      colorPalette="green"
      variant="subtle"
      onClick={onClick}
      cursor={!onClick ? 'default' : 'pointer'}
      _active={activeShake ? { transform: 'translateY(2px)' } : undefined}
      transition="all 0.2s"
      size={size}
      boxSize={computedBoxSize}
      w={giveWidth}
      h={giveWidth}
      bg="grey.50"
      {...props}
    >
      {icon && (
        <Float
          placement="bottom-end"
          transform="translate(-25%, -50%)"
          fontSize={floatFontSize}
          {...iconFloatProps}
        >
          {icon}
        </Float>
      )}
      {/* TODO amo: Move to icon prop */}
      {showUserError && (
        <Float
          placement="bottom-end"
          offsetX="0.5em"
          transform="translateY(-50%)"
          fontSize={floatFontSize}
        >
          <Box color="fg.error" fontSize="1.8em">
            <ErrorIcon />
          </Box>
        </Float>
      )}
    </AvatarPure>
  );
});

export const AvatarPure = memo(function PureAvatar({
  children,
  src,
  icon,
  iconFloatProps,
  showUserError,
  floatFontSize,
  ...props
}: React.PropsWithChildren<
  AvatarPrimitive.RootProps & {
    src?: string;
    user_id?: string;
    icon?: React.ReactNode;
    iconFloatProps?: FloatProps;
    showUserError?: boolean;
    floatFontSize?: string;
  }
>) {
  const [avatarSrc, setAvatarSrc] = useState(src || DefAvatarIcon);

  useLayoutEffect(() => {
    if (src === StateEnum.DELETE_ACCOUNT.toString()) {
      setAvatarSrc(DeletedAccountIcon);
    } else if (src) {
      const img = new Image();
      img.src = src;
      const cb = () => {
        setAvatarSrc(src);
      };
      img.addEventListener('load', cb, { once: true });
      return () => {
        img.removeEventListener('load', cb);
      };
    }
  }, [src]);

  return (
    <AvatarPrimitive.Root {...props}>
      <AvatarPrimitive.Image src={avatarSrc} />
      {children}
    </AvatarPrimitive.Root>
  );
});

export { DeletedAccountIcon, DefAvatarIcon as DefaultAvatarIcon };
